// Bloque «Cancelación» del tab Venta: cuándo, quién, motivo, condición por pieza y la devolución.
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { SaleStatusEnum } from '@/domains/sales/wizard/enums';
import type { BackofficeSale } from '@/api/services/retail-wizard/contract';
import { returnedPiecesOf, type ReturnedPiece } from '../../lib/returnedPieces';
import type { Translate } from '@/shared/patterns/CancelSale';
import { PendingDetailRow } from '@/domains/sales/portal/mysales/components/SaleDetailSections';
import { InfoRow, TabSection, dash, formatFechaHora } from './VentaTab';

/** El registro guarda el usuario de Cognito, no su nombre: se dice en el tooltip, no se pinta el id. */
export const ACTOR_WITHOUT_NAME_NOTE =
    'El registro guarda el identificador interno de quien lo hizo, no su nombre.';

export type CancellationSale = Pick<
    BackofficeSale,
    | 'status'
    | 'cancelledAt'
    | 'cancelReason'
    | 'lines'
    | 'returnConditions'
    | 'inventoryReturnPending'
>;

function pieceText(piece: ReturnedPiece, t: Translate): string {
    const condition =
        piece.condition === 'DAMAGED'
            ? t('cancelacion.danada', { defaultValue: 'dañada' })
            : t('cancelacion.buena', { defaultValue: 'buena' });
    const base = `${piece.label} ${condition}`;
    return piece.qty === undefined ? base : `${base} ×${String(piece.qty)}`;
}

export function CancellationSection({ sale }: { sale: CancellationSale }) {
    const { t } = useTranslation('ventas');
    if (sale.status !== SaleStatusEnum.CANCELLED) return null;

    const pieces = returnedPiecesOf(sale.lines, sale.returnConditions, t);

    return (
        <TabSection title={t('cancelacion.titulo', { defaultValue: 'Cancelación' })}>
            <InfoRow
                label={t('cancelacion.fecha', { defaultValue: 'Fecha y hora' })}
                value={sale.cancelledAt ? formatFechaHora(sale.cancelledAt) : '—'}
            />
            <PendingDetailRow
                label={t('cancelacion.quien', { defaultValue: 'Canceló' })}
                reason={ACTOR_WITHOUT_NAME_NOTE}
            />
            <InfoRow
                label={t('cancelacion.motivo', { defaultValue: 'Motivo' })}
                value={dash(sale.cancelReason)}
            />
            <InfoRow
                label={t('cancelacion.devuelto', { defaultValue: 'Lo devuelto' })}
                value={
                    pieces.length === 0 ? (
                        t('cancelacion.sinPiezas', { defaultValue: 'Sin detalle por pieza' })
                    ) : (
                        <span className="flex flex-col">
                            {pieces.map((piece) => (
                                <span
                                    key={piece.key}
                                    title={[piece.identifier, piece.reason]
                                        .filter(Boolean)
                                        .join(' · ')}
                                    className={cn(
                                        piece.condition === 'DAMAGED' && 'text-destructive',
                                    )}
                                >
                                    {pieceText(piece, t)}
                                </span>
                            ))}
                        </span>
                    )
                }
            />
            <InfoRow
                label={t('cancelacion.inventario', { defaultValue: 'Devolución a inventario' })}
                value={
                    sale.inventoryReturnPending
                        ? t('cancelacion.retenida', {
                              defaultValue: 'Retenida hasta que se cancele el crédito',
                          })
                        : t('cancelacion.devuelta', { defaultValue: 'Devuelta al inventario' })
                }
            />
        </TabSection>
    );
}
